import { Delete } from "@mui/icons-material";
import {
    AppBar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Step,
    StepLabel,
    Stepper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Toolbar,
    Typography,
} from "@mui/material";

import { DataGrid, deDE, GridCloseIcon } from "@mui/x-data-grid";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCreateContractBulk } from "../../../hooks/data/data_contract";
import { useUpdateAllocations } from "../../../hooks/data/data_allocation";
import { useGetOfferState, useGetOfferType } from "../../../hooks/data/data_offer";
import { IContract } from "../../../interfaces/IContract";
import { ICreateContract } from "../../../interfaces/ICreateContract";
import { ICreateContractBulk } from "../../../interfaces/ICreateContractBulk";
import { IOffer } from "../../../interfaces/IOffer";
import { IOfferPosition } from "../../../interfaces/IOfferPosition";
import { IOfferState } from "../../../interfaces/IOfferState";
import { IOfferType } from "../../../interfaces/IOfferType";
import { IPartner } from "../../../interfaces/IPartner";
import { IProject } from "../../../interfaces/IProject";
import { IUnit } from "../../../interfaces/IUnit";
import { IProjectPartnerAllocation } from "../../../interfaces/IProjectPartnerAllocation";
import { useSnackbar } from "../../context/SnackbarContext";
import { LoadingDialog } from "../../core/LoadingDialog";
import Transition from "../../core/Transition";
import CraftsAllocationTable from "./CraftsAllocationTable";
import OfferAssignmentDialog from "./OfferAssignmentDialog";
import { PartnerDialog } from "./PartnerDialog";
import { TextField } from "@mui/material";
import { AddOfferPositionsDialog } from "../../handoverProtocol/Childs/AddOfferPositionsDialog";
import { is } from "date-fns/locale";
import { roundToFloat } from "../../../utlis/Math";
import { CustomeTextField } from "../../core/CustomeTextField";

interface IProps {
    project: IProject;
    allPartners: IPartner[];
    offers: IOffer[];
    offerStates: IOfferState[];
    offerTypes: IOfferType[];
    units: IUnit[];
    createContract?: boolean;
    allocations?: IProjectPartnerAllocation[];
    unmount: () => void;
    refetchContracts: (options?: RefetchOptions) => Promise<QueryObserverResult<IContract[], Error>>;
    refetchAllocations: (options?: RefetchOptions) => Promise<QueryObserverResult<IProjectPartnerAllocation[], Error>>;
}

// Declare Partnerprice as a type
type Partnerprice = Record<number, { ek: number; vk: number }>;

const PositionPartnerAllocationDialog = ({
    project,
    allPartners,
    offers,
    units,
    createContract = true,
    offerStates,
    offerTypes,
    allocations,
    unmount,
    refetchContracts,
    refetchAllocations,
}: IProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const { showFeedback } = useSnackbar();
    const { createContractBulk } = useCreateContractBulk();
    const { updateAllocations } = useUpdateAllocations();

    const [isOfferDialogMount, setIsOfferDialogMount] = useState(false);
    const [isPartnerDialogMount, setIsPartnerDialogMount] = useState(false);
    const [isOfferAssignmentDialogMount, setIsOfferAssignmentDialogMount] = useState(true);

    const [selectedOffer, setSelectedOffer] = useState<IOffer | null>(null);
    const [selectedPositions, setSelectedPositions] = useState<IOfferPosition[]>([]);
    const [selectedPartners, setSelectedPartners] = useState<IPartner[]>([]);

    const [overrideAllocation, setOverrideAllocation] = useState<boolean>(true);
    const [useExistingAllocation, setUseExistingAllocation] = useState<boolean>(true);
    const [partnerPrices, setPartnerPrices] = useState<Partnerprice>({});

    const pricePerHourForPartner = useCallback(
        (partnerId: number): number => {
            // 1) explizit gesetzter EK
            const explicit = partnerPrices[partnerId]?.ek;
            return explicit;
        },
        [partnerPrices],
    );

    console.log("partnerPrices", partnerPrices);
    console.log("selectedPartners", selectedPartners);

    const buildAllocationsFromSelection = useCallback(
        (): IProjectPartnerAllocation[] =>
            selectedPartners
                .map((partner) => {
                    const ek = roundToFloat(partnerPrices[partner.id]?.ek, 2) ?? 0;
                    const vk =
                        roundToFloat(partnerPrices[partner.id]?.vk, 2) ?? roundToFloat(partner.selling_price, 2) ?? 0;
                    return {
                        id: 0,
                        Partner: partner.id,
                        positions: selectedPositions.filter((pos) => pos.Partner === partner.id).map((pos) => pos.id),
                        selling_price_ek:
                            ek !== 0 && ek !== undefined && ek !== null
                                ? ek
                                : roundToFloat(
                                      selectedPositions.filter((pos) => pos.Partner === partner.id)[0]
                                          ?.price_per_hour ?? 0,
                                      2,
                                  ) || 0,
                        selling_price_vk: vk,
                    };
                })
                .filter((allocation) => allocation.positions.length > 0),
        [selectedPartners, selectedPositions, partnerPrices],
    );

    const overwriteTouchedPositions = (
        existing: IProjectPartnerAllocation[] | undefined,
        fresh: IProjectPartnerAllocation[],
    ): IProjectPartnerAllocation[] => {
        if (!existing) return fresh; // nichts da? einfach neue zurück

        const touched = new Set<number>(fresh.flatMap((a) => a.positions));

        const trimmed = existing
            .map((alloc) => {
                const remaining = alloc.positions.filter((pid) => !touched.has(pid));
                return remaining.length > 0 ? { ...alloc, positions: remaining } : null;
            })
            .filter(Boolean) as IProjectPartnerAllocation[];

        return [...trimmed, ...fresh];
    };

    const buildContracts = useCallback(
        (positions: IOfferPosition[]): ICreateContract[] => {
            type Key = `${number}-${number}`; // offerId-partnerId
            const grouped = new Map<Key, IOfferPosition[]>();

            positions.forEach((pos) => {
                if (!pos.Partner) return; // skip unassigned
                const key = `${pos.Offer}-${pos.Partner}` as Key;
                const existing = grouped.get(key) ?? [];
                existing.push(pos);
                grouped.set(key, existing);
            });

            const contracts: ICreateContract[] = [];

            grouped.forEach((groupPositions, key) => {
                const [offerIdStr, partnerIdStr] = key.split("-");
                const offerId = Number(offerIdStr);
                const partnerId = Number(partnerIdStr);
                const pph = pricePerHourForPartner(partnerId);

                contracts.push({
                    Partner: partnerId,
                    ContractType: 30, // hard‑coded – adjust if needed
                    payload_id: offerId,
                    Customer: project.Participant.filter((p) => p.is_contact_person)[0]?.Customer ?? null, // use first contact person customer
                    ContractPositions: groupPositions.map((p) => ({
                        id: p.id,
                        Position: p.id,
                        Service: p.Service,
                        Unit: p.Unit,
                        articelNumber: p.articelNumber,
                        title: p.title,
                        description: p.description,
                        units: p.units,
                        position: p.position,
                        timeFactor: p.timeFactor,
                        price_per_hour: roundToFloat(pph, 2) || 0,
                        price: roundToFloat((p.timeFactor ?? 1) * p.units * pph, 2) || 0,
                    })),
                    Offer: offerId,
                });
            });

            return contracts;
        },
        [pricePerHourForPartner],
    );

    const createContractsFromExistingAllocations = async (offer: IOffer) => {
        try {
            setIsLoading(true);
            const positionPartnerMap = new Map<number, number>();
            allocations?.forEach((allocation) => {
                allocation.positions.forEach((posId) => positionPartnerMap.set(posId, allocation.Partner));
            });

            const positionsWithPartner: IOfferPosition[] = offer.OfferPositions.map((pos) => ({
                ...pos,
                Partner: positionPartnerMap.get(pos.id) ?? null,
            }));

            const contracts = buildContracts(positionsWithPartner);
            const payload: ICreateContractBulk = {
                Project: project.id,
                CreateContractArray: contracts,
            };

            await createContractBulk(payload);
            await refetchContracts();
            showFeedback("Die Verträge wurden erstellt!", "success");
        } catch (e) {
            console.error(e);
            showFeedback("Es ist ein Fehler aufgetreten!", "error");
        } finally {
            setIsLoading(false);
            unmount();
        }
    };

    const onSubmit = useCallback(async () => {
        try {
            setIsLoading(true);

            const newAllocations = buildAllocationsFromSelection();

            const combinedAllocations = overrideAllocation
                ? overwriteTouchedPositions(allocations, newAllocations)
                : [
                      ...(allocations ?? []).filter(
                          (a) => !newAllocations.some((n) => n.positions.some((pid) => a.positions.includes(pid))),
                      ),
                      ...newAllocations,
                  ];

            if (createContract) {
                const contractSource = selectedPositions.filter((pos) => pos.Partner !== null);
                const contracts = buildContracts(contractSource);
                const contractsPayload: ICreateContractBulk = {
                    Project: project.id,
                    CreateContractArray: contracts,
                };
                if (overrideAllocation) {
                    await Promise.all([
                        createContractBulk(contractsPayload),
                        updateAllocations({ allocationObject: combinedAllocations, projectId: project.id }),
                    ]);
                    await Promise.all([refetchContracts(), refetchAllocations()]);
                } else {
                    await createContractBulk(contractsPayload);
                    await refetchContracts();
                }

                await showFeedback("Die Verträge wurden erstellt!", "success");
            }

            if (overrideAllocation || !createContract) {
                await updateAllocations({
                    allocationObject: combinedAllocations,
                    projectId: project.id,
                });
                await refetchAllocations();
                showFeedback("Die Allocations wurden aktualisiert!", "success");
            }
        } catch (error) {
            console.error(error);
            showFeedback("Es ist ein Fehler aufgetreten!", "error");
        } finally {
            setIsLoading(false);
            unmount();
        }
    }, [
        allocations,
        buildAllocationsFromSelection,
        buildContracts,
        createContract,
        createContractBulk,
        overrideAllocation,
        project.id,
        refetchAllocations,
        refetchContracts,
        selectedPositions,
        showFeedback,
        unmount,
        updateAllocations,
    ]);

    useEffect(() => {
        if (!useExistingAllocation || !allocations || allocations.length === 0) {
            setPartnerPrices({
                ...allPartners.reduce(
                    (acc, partner) => {
                        acc[partner.id] = {
                            ek: partner.selling_price || 0,
                            vk: partner.selling_price || 0,
                        };
                        return acc;
                    },
                    {} as Record<number, { ek: number; vk: number }>,
                ),
            });
            return;
        }

        const allocationPartnerIds = Array.from(new Set(allocations.map((a) => a.Partner)));
        setSelectedPartners(allPartners.filter((p) => allocationPartnerIds.includes(p.id)));
        const prices: Record<number, { ek: number; vk: number }> = {};
        allocations.forEach((allocation) => {
            const partnerId = allocation.Partner;
            prices[partnerId] = {
                ek: allocation.selling_price_ek,
                vk: allocation.selling_price_vk,
            };
        });
        setPartnerPrices(prices);
        const allPositions = offers.flatMap((offer) => offer.OfferPositions);
        const positionMap = new Map(allPositions.map((pos) => [pos.id, pos]));
        const allocatedPositions = allocations
            .flatMap((allocation) =>
                allocation.positions.map((posId) => {
                    const pos = positionMap.get(posId);
                    return pos ? { ...pos, Partner: allocation.Partner } : null;
                }),
            )
            .filter(Boolean) as IOfferPosition[];

        setSelectedPositions(allocatedPositions);
    }, [allocations, allPartners, offers, useExistingAllocation]);

    const areUnassignedPositions = useMemo(() => {
        return selectedPositions.some((x) => x.Partner === null) && overrideAllocation;
    }, [selectedPositions, overrideAllocation]);

    if (isLoading) return <LoadingDialog fullScreen />;

    return (
        <>
            {(!isOfferAssignmentDialogMount || !createContract) && (
                <Dialog fullScreen open TransitionComponent={Transition} onClose={unmount}>
                    <AppBar sx={{ position: "relative" }}>
                        <Toolbar>
                            <IconButton edge="start" color="inherit" onClick={unmount} aria-label="close">
                                <GridCloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                                {createContract
                                    ? "Zuweisung von Handwerkerbeauftragungen"
                                    : "Zuweisung von Projekt-Allocations"}
                            </Typography>
                        </Toolbar>
                    </AppBar>

                    <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
                        <DialogContent sx={{ flex: 1 }}>
                            <Typography variant="h5">
                                Zugeordnete Partner
                                <Button
                                    variant="outlined"
                                    sx={{ float: "right", mr: 2 }}
                                    onClick={() => setIsPartnerDialogMount(true)}
                                >
                                    Partner hinzufügen
                                </Button>
                            </Typography>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Partner</TableCell>
                                        <TableCell align="right">EK €</TableCell>
                                        <TableCell align="right">VK €</TableCell>
                                        <TableCell sx={{ width: 120 }}>Aktionen</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {selectedPartners.map((p) => (
                                        <TableRow key={p.id}>
                                            <TableCell>{p.Contact_Person.Company?.company_name}</TableCell>

                                            <TableCell align="right">
                                                <CustomeTextField
                                                    type="euro"
                                                    attr="ek"
                                                    object={partnerPrices[p.id] ?? { ek: 0, vk: 0 }}
                                                    setObject={(value: { ek?: number; vk?: number }) =>
                                                        setPartnerPrices((prev) => ({
                                                            ...prev,
                                                            [p.id]: { ...prev[p.id], ek: Number(value?.ek ?? 0) },
                                                        }))
                                                    }
                                                    size="small"
                                                    label="Einkaufspreis"
                                                />
                                            </TableCell>

                                            <TableCell align="right">
                                                <CustomeTextField
                                                    type="euro"
                                                    attr="vk"
                                                    object={partnerPrices[p.id] ?? { ek: 0, vk: 0 }}
                                                    setObject={(value: { ek?: number; vk?: number }) =>
                                                        setPartnerPrices((prev) => ({
                                                            ...prev,
                                                            [p.id]: { ...prev[p.id], vk: Number(value?.vk ?? 0) },
                                                        }))
                                                    }
                                                    size="small"
                                                    label="Verkaufspreis"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ width: 120 }}>
                                                <IconButton
                                                    onClick={() => {
                                                        setSelectedPartners((prev) =>
                                                            prev.filter((x) => x.id !== p.id),
                                                        );
                                                        setSelectedPositions((prev) =>
                                                            prev.map((pos) =>
                                                                pos.Partner === p.id ? { ...pos, Partner: null } : pos,
                                                            ),
                                                        );
                                                    }}
                                                    color="error"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            <Box sx={{ mt: 5 }} />
                            <Typography variant="h5">
                                Angebotspositionen
                                <Button
                                    variant="outlined"
                                    sx={{ float: "right", mr: 2 }}
                                    onClick={() => setIsOfferDialogMount(true)}
                                >
                                    Positionen aus Angebot hinzufügen
                                </Button>
                            </Typography>
                            <Box sx={{ mt: 3 }} />
                            {selectedPositions.filter((x) => x.Partner === null).length > 0 && (
                                <CraftsAllocationTable
                                    key="unassigned"
                                    title="Ohne Partner zugewiesen"
                                    selectedPositions={selectedPositions.filter((x) => x.Partner === null)}
                                    selectedPartners={selectedPartners}
                                    offers={offers}
                                    units={units}
                                    isNoPartner={true}
                                    setSelectedPositions={setSelectedPositions}
                                />
                            )}
                            {selectedPartners.map((p) => (
                                <React.Fragment key={p.id}>
                                    <Box sx={{ mt: 3 }} />
                                    <CraftsAllocationTable
                                        key={p.id}
                                        title={p.Contact_Person.Company?.company_name ?? "Partner ohne Namen"}
                                        selectedPositions={selectedPositions.filter((x) => x.Partner === p.id)}
                                        selectedPartners={selectedPartners}
                                        offers={offers}
                                        units={units}
                                        setSelectedPositions={setSelectedPositions}
                                    />
                                </React.Fragment>
                            ))}
                        </DialogContent>

                        <DialogActions sx={{ borderTop: "1px solid #e0e0e0" }}>
                            <Button variant="outlined" onClick={unmount}>
                                Schließen
                            </Button>
                            <Button
                                variant="contained"
                                disabled={
                                    selectedPositions.length === 0 ||
                                    selectedPartners.length === 0 ||
                                    areUnassignedPositions ||
                                    isLoading
                                }
                                onClick={onSubmit}
                                type="submit"
                                sx={{ ml: 2 }}
                            >
                                {isLoading ? (
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <span>Speichern</span>
                                        <CircularProgress size={20} />
                                    </Box>
                                ) : (
                                    "Speichern"
                                )}
                            </Button>
                        </DialogActions>
                    </Box>
                </Dialog>
            )}

            {isOfferAssignmentDialogMount && createContract && (
                <OfferAssignmentDialog
                    onAbort={() => {
                        setIsOfferAssignmentDialogMount(false);
                        unmount();
                    }}
                    onFinish={(offer, partners, overwriteAlloc, useExisting) => {
                        setSelectedOffer(offer);
                        setOverrideAllocation(overwriteAlloc);
                        setUseExistingAllocation(useExisting);

                        if (useExisting) {
                            createContractsFromExistingAllocations(offer);
                            return;
                        }

                        setSelectedPartners(partners);
                        setSelectedPositions(
                            offer.OfferPositions.map((position) => ({
                                ...position,
                                Partner: partners.find((partner) => partner.id === position.Partner)?.id ?? null,
                                Offer: offer.id,
                            })),
                        );
                        setIsOfferAssignmentDialogMount(false);
                        setIsOfferDialogMount(false);
                    }}
                    offers={offers}
                    allPartners={allPartners}
                    allocations={allocations}
                />
            )}

            {isPartnerDialogMount && (
                <PartnerDialog
                    allPartners={allPartners}
                    selectedPartners={selectedPartners}
                    setSelectedPartners={setSelectedPartners}
                    unmount={() => setIsPartnerDialogMount(false)}
                />
            )}
            {isOfferDialogMount && (
                <AddOfferPositionsDialog
                    defaultPartnerId={null}
                    offers={offers}
                    offerStates={offerStates}
                    offerTypes={offerTypes}
                    setPositions={setSelectedPositions}
                    unmount={() => setIsOfferDialogMount(false)}
                />
            )}
        </>
    );
};

export default PositionPartnerAllocationDialog;
